/* eslint-disable react/no-danger */
import React, { useContext } from 'react';
import classNames from 'classnames';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Col, Row } from '@thd-olt-component-react/grid';
import { Heading } from '@thd-olt-component-react/heading';
import { hoist, QueryContext, QueryProvider } from '@thd-nucleus/data-sources';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { CollectionPod } from '@thd-olt-component-react/home-services';
import { CustomProductPodDetails, ProductPodGroup } from '@thd-olt-component-react/product-pod-group';
import { IntegratedBrandPod } from './IntegratedBrandPod';
import { PriceRange } from '../price-range/PriceRange';
import { collectionQueryOption, cleanHtml } from '../CollectionHelpers';
import { IntegratedBrandPropTypes } from './IntegratedBrandPropTypes';
import './integrated-brand.scss';

const HoistedIntegratedBrandPodGroup = hoist(ProductPodGroup, IntegratedBrandPod);

export const IntegratedBrandCollectionDesktop = ({ collection, collectionId, storeId, skipFn }) => {
  const {
    media,
    metadata,
    products
  } = collection || {};
  const {
    brandName,
    canonicalUrl,
    collectionDescription,
    name,
  } = metadata || {};
  const { defaultVariables } = useContext(QueryContext);
  const centered = !media;
  const collectionDetailContainer = classNames(
    { 'collections_details--center': centered }
  );
  const filteredProducts = products?.filter((product) => product?.pricing?.value);
  const itemIdOfFirstProduct = filteredProducts?.[0]?.itemId;
  const allProducts = filteredProducts?.length;
  const title = <span>Shop this Collection <span className="collections__count">({allProducts})</span></span>;
  return (
    <Row className="integrated-brand-collection">
      {media && (
        <Col tag={Col} xs="6">
          <ErrorBoundary name="collection-media-gallery">
            <MediaGalleryDesktop collectionId={collectionId} />
          </ErrorBoundary>
        </Col>
      )}
      <Col tag={Col} xs={media ? 6 : 12} className={collectionDetailContainer}>
        <ErrorBoundary name="collection-details">
          <h2 className="product-header__brand product-header__brand--medium">
            <a href={canonicalUrl}>{brandName}</a>
          </h2>
          <h1 className="product-header__collection-name product-header__collection-name--husky">{name}</h1>
          <div className="price__wrapper">
            <PriceRange products={filteredProducts} numberOfProduct={allProducts} />
          </div>
          <div dangerouslySetInnerHTML={cleanHtml(collectionDescription)} />
          { itemIdOfFirstProduct && (
            <QueryProvider
              cacheKey="home-services"
              defaultVariables={{ skipSubscribeAndSave: true }}
            >
              <CollectionPod itemId={itemIdOfFirstProduct} storeId={storeId} />
            </QueryProvider>
          ) }
        </ErrorBoundary>
      </Col>
      <Col>
        <ErrorBoundary name="collections-shop">
          <Heading underline>
            {title}
          </Heading>
          <Row className="integrated-brand-collection__pods">
            <QueryProvider
              dataSource="Collection"
              defaultVariables={{
                skipSubscribeAndSave: true,
                skipInstallServices: true,
                storeId,
                skipKPF: true,
                ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean'
                  && { isBrandPricingPolicyCompliant:
                    defaultVariables.current.isBrandPricingPolicyCompliant })
              }}
              queryOptions={collectionQueryOption}
              skip={skipFn}
              cacheKey="ibt-desktop"
            >
              <HoistedIntegratedBrandPodGroup DetailsPod={CustomProductPodDetails}>
                {(filteredProducts || []).map((product, productIndex) => {
                  const itemId = product.identifiers?.itemId;
                  return (
                    <Col
                      xs={6}
                      className="side-by-side nopadding"
                      key={productIndex}
                    >
                      <ErrorBoundary name={`integrated-brand-pod-${productIndex}`}>
                        <IntegratedBrandPod
                          itemId={itemId}
                          storeId={storeId}
                          position={productIndex + 1}
                          parent="collection"
                          url={canonicalUrl}
                        />
                      </ErrorBoundary>
                    </Col>
                  );
                })}
              </HoistedIntegratedBrandPodGroup>
            </QueryProvider>
          </Row>
        </ErrorBoundary>
      </Col>
    </Row>
  );
};

IntegratedBrandCollectionDesktop.propTypes = IntegratedBrandPropTypes;